"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSubmitApprovalMutationQuery = exports.useCancelApprovalMutationQuery = exports.useForwardRequestMutation = exports.useGetTeamApproversQuery = exports.useDeclinePendingApprovalMutationQuery = exports.useCloseStuckPendingApprovalMutation = exports.updateApprovalsData = exports.getCombinedApprovalsData = void 0;
const react_query_1 = require("@tanstack/react-query");
const axios_1 = require("axios");
const immer_1 = require("immer");
const lodash_1 = __importDefault(require("lodash"));
const pino_1 = __importDefault(require("pino"));
const approvalHelpers_1 = require("../../components/pages/approvals/approvalHelpers");
const appConstants_1 = require("../../constants/appConstants");
const events_1 = require("../../constants/events");
const strings_1 = require("../../constants/strings");
const approvalRepository_1 = require("../../repositories/approvalRepository");
const Root_1 = require("../../router/Root");
const userSelectors_1 = require("../../selectors/userSelectors");
const translationUtil_1 = require("../../utils/translationUtil");
const zustandStore_1 = require("../../zustandStore");
const query_keys_1 = require("../query-keys");
const approvalsUtils_1 = require("./approvalsUtils");
const logger = (0, pino_1.default)();
const approvalKeys = {
    getApprovals: (state) => ['approvals', state],
    closeStuckPendingApproval: (stuckPendingApprovalId) => ['approvals', stuckPendingApprovalId, 'close'],
    combinedApprovalsData: isActivityPage => ['combinedApprovalsData', isActivityPage],
    getTeamApprovers: (teamId, approverId) => ['approvals', teamId, 'teamId', approverId, 'approverId'],
};
const approvalsQuery = (state) => (0, react_query_1.queryOptions)({
    queryKey: approvalKeys.getApprovals(state),
    queryFn: () => approvalRepository_1.ApprovalRepository.getApprovals(state),
    staleTime: Number.POSITIVE_INFINITY,
});
const fetchAndMergeApprovals = async () => {
    const approvalsResults = await Promise.allSettled([
        Root_1.queryClient.fetchQuery(approvalsQuery('assigned')),
        Root_1.queryClient.fetchQuery(approvalsQuery('completed')),
    ]);
    const assignedApprovalsResponse = approvalsResults[0].status === 'fulfilled' ? approvalsResults[0].value : { approvals: [] };
    const completedApprovalsResponse = approvalsResults[1].status === 'fulfilled' ? approvalsResults[1].value : { approvals: [] };
    approvalsResults.forEach((result, index) => {
        if (result.status === 'rejected') {
            logger.error(`Error fetching ${index === 0 ? 'assigned' : 'completed'} approvals:`, result.reason);
        }
    });
    return {
        assignedApprovals: assignedApprovalsResponse.approvals,
        completedApprovals: completedApprovalsResponse.approvals,
    };
};
const getCombinedApprovalsData = (user, teamId, isActivityPage) => {
    const queryKey = approvalKeys.combinedApprovalsData(isActivityPage);
    const isUserCompanyOrSupportAdmin = (0, userSelectors_1.isCompanyOrSupportAdmin)(user?.roles ?? []);
    const queryFn = async () => {
        const cachedData = Root_1.queryClient.getQueryData(queryKey);
        if (cachedData) {
            return cachedData;
        }
        const { assignedApprovals, completedApprovals } = await fetchAndMergeApprovals();
        const initialCombinedApprovals = [...assignedApprovals, ...completedApprovals];
        const currentCombinedLists = (0, approvalsUtils_1.getCurrentCombinedLists)({ initialCombinedApprovals });
        const updatedApprovalItems = (0, approvalsUtils_1.updateApprovalItems)({
            currentCombinedLists,
            teamId,
            isUserCompanyOrSupportAdmin,
            isActivityPage,
            user,
        });
        const allUpdatedApprovalItems = isActivityPage
            ? {
                approvals: updatedApprovalItems.approvalsDashBoardCollection,
                count: updatedApprovalItems.approvalsDashBoardCollection.length,
            }
            : {
                approvals: updatedApprovalItems.approvalsPageCollection,
                count: updatedApprovalItems.approvalsPageCollection.length,
            };
        const groupedApprovals = (0, approvalsUtils_1.groupApprovals)(allUpdatedApprovalItems.approvals, isActivityPage);
        const approvalStates = ['pending', 'approved', 'declined', 'canceled'];
        const approvalsCount = (0, approvalsUtils_1.getApprovalsCountGroups)(groupedApprovals, approvalStates);
        return {
            assignedApprovals,
            completedApprovals,
            groupedApprovals,
            approvalsCount,
            approvalsDashBoardCollection: updatedApprovalItems.approvalsDashBoardCollection,
            isActivityPage,
        };
    };
    return {
        queryKey,
        queryFn,
        staleTime: Number.POSITIVE_INFINITY,
        enabled: Boolean(user && user?.userId && teamId),
        retry: (failureCount, error) => {
            logger.error(error);
            return failureCount < 3 && (0, axios_1.isAxiosError)(error) && error.status !== 404;
        },
    };
};
exports.getCombinedApprovalsData = getCombinedApprovalsData;
const findIndexByRequestId = (approvals, requestId) => approvals.findIndex(approval => approval.request?.requestId === requestId);
const updateApprovalsData = async (user, streamDataEvents) => {
    const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
    const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
    const previousApprovalsData = Root_1.queryClient.getQueryData(queryKeyApproval);
    const previousActivityData = Root_1.queryClient.getQueryData(queryKeyActivity);
    if (!previousApprovalsData && !previousActivityData) {
        return;
    }
    if (streamDataEvents.length === 0) {
        return;
    }
    const approvalsUiEvents = new Set([
        events_1.events.types.purchases.approvalTask,
        events_1.events.types.purchases.virtualCardAdded,
        events_1.events.types.purchases.requestCancelled,
        events_1.events.types.purchases.requestRejected,
    ]);
    const approvalsFromStream = streamDataEvents
        .filter(({ type }) => approvalsUiEvents.has(type))
        .map(({ data, type, requestId }) => {
        const task = data.task || data;
        return { ...task, type, requestId };
    });
    if (approvalsFromStream.length === 0) {
        return;
    }
    for (const streamApproval of approvalsFromStream) {
        const requestId = streamApproval.requestId || 'N/A';
        const { type } = streamApproval;
        switch (type) {
            case events_1.events.types.purchases.approvalTask: {
                await handleApprovalTask(queryKeyApproval, user, streamApproval, requestId);
                await handleApprovalTask(queryKeyActivity, user, streamApproval, requestId);
                break;
            }
            case events_1.events.types.purchases.virtualCardAdded: {
                await handleVirtualCardAdded(queryKeyApproval, user, streamApproval, requestId);
                await handleVirtualCardAdded(queryKeyActivity, user, streamApproval, requestId);
                break;
            }
            case events_1.events.types.purchases.requestCancelled: {
                await handleRequestCancelled(queryKeyApproval, streamApproval, requestId);
                await handleRequestCancelled(queryKeyActivity, streamApproval, requestId);
                break;
            }
            case events_1.events.types.purchases.requestRejected: {
                await handleRequestRejected(queryKeyApproval, streamApproval, requestId);
                await handleRequestRejected(queryKeyActivity, streamApproval, requestId);
                break;
            }
            default:
            // No default
        }
    }
};
exports.updateApprovalsData = updateApprovalsData;
async function handleApprovalTask(queryKey, user, streamApproval, requestId) {
    const previousData = Root_1.queryClient.getQueryData(queryKey);
    if (!previousData) {
        logger.warn('no previous Data for approvals');
        return;
    }
    // set new dashboardApprovals new data
    const previousTeamData = Root_1.queryClient.getQueryData([query_keys_1.userKeys.getUserSettings, 'selectedTeamID']);
    const isUserCompanyOrSupportAdmin = (0, userSelectors_1.isCompanyOrSupportAdmin)(user?.roles ?? []);
    const previousDashboardApprovals = previousData?.approvalsDashBoardCollection || [];
    const currentCombinedLists = [...previousDashboardApprovals, streamApproval];
    const newApprovalsDashBoardCollection = isUserCompanyOrSupportAdmin
        ? currentCombinedLists
        : currentCombinedLists.filter(approval => approval.request.teamId === previousTeamData?.[0]);
    if (queryKey?.[1] || (0, approvalsUtils_1.isUserAssignedInTheAproval)(user?.userId, streamApproval) || (0, userSelectors_1.isCompanyAdmin)(user?.roles || [])) {
        const pending = previousData.groupedApprovals.pending;
        if (findIndexByRequestId(pending.approvals, requestId) === -1) {
            await updateQueryData(previousData, queryKey, draft => {
                draft.groupedApprovals.pending.approvals.push({
                    ...streamApproval,
                    isPending: true,
                    isApproved: false,
                    isDeclined: false,
                    isCancelled: false,
                });
                draft.groupedApprovals.pending.count += 1;
                draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
            });
        }
        else if (streamApproval.reassignedToUser && !(0, approvalHelpers_1.isReassignedToSelf)(streamApproval)) {
            // the current user forwarded the approval to another user
            // first forward and new event will come to this branch
            const { pending } = previousData.groupedApprovals;
            const pendingIndex = findIndexByRequestId(pending.approvals, requestId);
            if (pendingIndex !== -1) {
                const existingApproval = pending.approvals[pendingIndex];
                if (!existingApproval) {
                    return;
                }
                const updatedApproval = {
                    ...existingApproval,
                    ...streamApproval,
                };
                await updateQueryData(previousData, queryKey, draft => {
                    draft.groupedApprovals.pending.approvals[pendingIndex] = {
                        ...updatedApproval,
                        isPending: true,
                        isApproved: false,
                        isDeclined: false,
                        isCancelled: false,
                    };
                    draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
                });
            }
        }
        else if (streamApproval.reassignedByUser) {
            // other event after first forward
            // here we get the new approval id
            // here we get also the first reassign event
            const shouldCheckReassignedToSelf = queryKey === approvalKeys.combinedApprovalsData(false) ? (0, approvalHelpers_1.isReassignedToSelf)(streamApproval) : true;
            const { pending } = previousData.groupedApprovals;
            const pendingIndex = findIndexByRequestId(pending.approvals, requestId);
            if (shouldCheckReassignedToSelf && pendingIndex !== -1) {
                const existingApproval = pending.approvals[pendingIndex];
                if (!existingApproval) {
                    return;
                }
                const updatedApproval = {
                    ...existingApproval,
                    ...streamApproval,
                    outcome: existingApproval.outcome === 'REASSIGNED' ? streamApproval.outcome : 'REASSIGNED',
                };
                await updateQueryData(previousData, queryKey, draft => {
                    draft.groupedApprovals.pending.approvals[pendingIndex] = {
                        ...updatedApproval,
                        isPending: true,
                        isApproved: false,
                        isDeclined: false,
                        isCancelled: false,
                    };
                    draft.groupedApprovals.pending.count = draft.groupedApprovals.pending.approvals.length;
                    draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
                });
            }
        }
    }
    else {
        await updateQueryData(previousData, queryKey, draft => {
            draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
        });
    }
}
async function handleVirtualCardAdded(queryKey, user, streamApproval, requestId) {
    const previousData = Root_1.queryClient.getQueryData(queryKey);
    if (!previousData) {
        logger.warn('no previous Data for approvals');
        return;
    }
    const { pending } = previousData.groupedApprovals;
    const pendingIndex = findIndexByRequestId(pending.approvals, requestId);
    /*
     * sometimes we get straight the "purchases.cardAdded" event but not a previous event
     * for the same approval with the event purchases.approvalTask
     */
    const previousDashboardApprovals = previousData?.approvalsDashBoardCollection ?? [];
    const currentCombinedLists = previousDashboardApprovals.map(approval => approval.id === streamApproval.id ? streamApproval : approval);
    if (!currentCombinedLists.some(approval => approval.id === streamApproval.id)) {
        currentCombinedLists.push(streamApproval);
    }
    const isUserCompanyOrSupportAdmin = (0, userSelectors_1.isCompanyOrSupportAdmin)(user?.roles ?? []);
    const previousTeamData = Root_1.queryClient.getQueryData([query_keys_1.userKeys.getUserSettings, 'selectedTeamID']);
    const newApprovalsDashBoardCollection = isUserCompanyOrSupportAdmin
        ? currentCombinedLists
        : currentCombinedLists.filter(approval => approval.request.teamId === previousTeamData?.[0]);
    await (pendingIndex === -1
        ? updateQueryData(previousData, queryKey, draft => {
            draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
        })
        : updateQueryData(previousData, queryKey, draft => {
            draft.groupedApprovals.pending.approvals.splice(pendingIndex, 1);
            draft.groupedApprovals.pending.count -= 1;
            draft.groupedApprovals.approved.approvals.push({
                ...streamApproval,
                isPending: false,
                isApproved: true,
                isDeclined: false,
                isCancelled: false,
            });
            draft.groupedApprovals.approved.count += 1;
            draft.approvalsDashBoardCollection = newApprovalsDashBoardCollection;
        }));
}
async function handleRequestCancelled(queryKey, streamApproval, requestId) {
    const previousData = Root_1.queryClient.getQueryData(queryKey);
    if (!previousData) {
        logger.warn('no previous Data for approvals');
        return;
    }
    if (!lodash_1.default.isEmpty(streamApproval.request)) {
        const { approved } = previousData.groupedApprovals;
        const approvalIndex = findIndexByRequestId(approved.approvals, requestId);
        await updateQueryData(previousData, queryKey, draft => {
            const approvalToUpdate = draft.groupedApprovals.approved.approvals[approvalIndex];
            if (!approvalToUpdate) {
                return;
            }
            const formattedLastEdit = new Date(Date.now()).toISOString();
            approvalToUpdate.outcome = appConstants_1.requestConstants.outcomes.canceled;
            approvalToUpdate.request.lastEdit = formattedLastEdit;
            draft.groupedApprovals.canceled.approvals.push({
                ...approvalToUpdate,
                isPending: false,
                isApproved: false,
                isDeclined: false,
                isCancelled: true,
            });
            draft.groupedApprovals.canceled.count += 1;
            draft.groupedApprovals.approved.approvals.splice(approvalIndex, 1);
            draft.groupedApprovals.approved.count -= 1;
            draft.assignedApprovals = draft.assignedApprovals.filter(assignedApproval => assignedApproval.subjectId !== requestId);
        });
    }
}
async function handleRequestRejected(queryKey, streamApproval, requestId) {
    const previousData = Root_1.queryClient.getQueryData(queryKey);
    if (!previousData) {
        logger.warn('no previous Data for approvals');
        return;
    }
    const { pending } = previousData.groupedApprovals;
    const pendingIndex = findIndexByRequestId(pending.approvals, requestId);
    if (pendingIndex !== -1) {
        await updateQueryData(previousData, queryKey, draft => {
            const approvalToUpdate = draft.groupedApprovals.pending.approvals[pendingIndex];
            if (!approvalToUpdate) {
                return;
            }
            const formattedLastEdit = new Date(Date.now()).toISOString();
            approvalToUpdate.outcome = appConstants_1.requestConstants.outcomes.rejected;
            approvalToUpdate.request.state = streamApproval.state;
            approvalToUpdate.request.lastEdit = formattedLastEdit;
            draft.groupedApprovals.declined.approvals.push({
                ...approvalToUpdate,
                isPending: false,
                isApproved: false,
                isDeclined: true,
                isCancelled: false,
            });
            draft.groupedApprovals.declined.count += 1;
            draft.groupedApprovals.pending.approvals.splice(pendingIndex, 1);
            draft.groupedApprovals.pending.count -= 1;
            draft.assignedApprovals = draft.assignedApprovals.filter(assignedApproval => assignedApproval.subjectId !== requestId);
        });
    }
}
async function updateQueryData(previousData, queryKey, updateFn) {
    await Root_1.queryClient.cancelQueries({ queryKey });
    Root_1.queryClient.setQueryData(queryKey, (0, immer_1.produce)(previousData, updateFn));
    await Root_1.queryClient.invalidateQueries({
        queryKey,
        refetchType: 'all',
    });
    await Root_1.queryClient.invalidateQueries({
        queryKey: ['approval'],
        refetchType: 'all',
    });
}
const useCloseStuckPendingApprovalMutation = () => {
    // check if required
    return (0, react_query_1.useMutation)({
        mutationFn: (stuckPendingApprovalId) => approvalRepository_1.ApprovalRepository.closeStuckPendingApproval(stuckPendingApprovalId),
        onError: err => {
            logger.error(err);
        },
    });
};
exports.useCloseStuckPendingApprovalMutation = useCloseStuckPendingApprovalMutation;
const updateOnDeclineData = async (subjectId) => {
    const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
    const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
    const queryKeys = [queryKeyActivity, queryKeyApproval];
    const previousDataCollection = await Promise.all(queryKeys.map(async (queryKey) => {
        await Root_1.queryClient.cancelQueries({ queryKey });
        const previousData = Root_1.queryClient.getQueryData(queryKey);
        if (previousData) {
            const pendingApprovals = previousData.groupedApprovals.pending.approvals;
            const approvalIndex = pendingApprovals.findIndex(approval => approval.subjectId === subjectId);
            if (approvalIndex !== -1) {
                await updateQueryData(previousData, queryKey, draft => {
                    const approvalToUpdate = draft.groupedApprovals.pending.approvals[approvalIndex];
                    if (!approvalToUpdate) {
                        return;
                    }
                    const formattedLastEdit = new Date(Date.now()).toISOString();
                    approvalToUpdate.outcome = appConstants_1.requestConstants.outcomes.rejected;
                    approvalToUpdate.request.state = appConstants_1.requestConstants.outcomes.rejected;
                    approvalToUpdate.request.lastEdit = formattedLastEdit;
                    draft.groupedApprovals.declined.approvals.push({
                        ...approvalToUpdate,
                        isPending: false,
                        isApproved: false,
                        isDeclined: true,
                        isCancelled: false,
                    });
                    draft.groupedApprovals.declined.count += 1;
                    draft.groupedApprovals.pending.approvals.splice(approvalIndex, 1);
                    draft.groupedApprovals.pending.count -= 1;
                    draft.assignedApprovals = draft.assignedApprovals.filter(assignedApproval => assignedApproval.subjectId !== subjectId);
                });
            }
        }
        return previousData;
    }));
    return previousDataCollection;
};
const useDeclinePendingApprovalMutationQuery = () => {
    const { setNotification, setShowConversationButtons, setPageLoading } = (0, zustandStore_1.useZustand)();
    const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
    const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
    return (0, react_query_1.useMutation)({
        mutationFn: approvalRepository_1.ApprovalRepository.declinePendingApproval,
        onMutate: async ({ subjectId }) => {
            setPageLoading(true);
            const previousCollection = await updateOnDeclineData(subjectId);
            return { previousData: previousCollection };
        },
        onSuccess: async () => {
            await Root_1.queryClient.invalidateQueries({
                queryKey: query_keys_1.requestKeys.getRequestList(),
            });
            setShowConversationButtons(false);
            setNotification([
                {
                    type: strings_1.notificationStrings.SUCCESS,
                    message: 'Request was declined.',
                },
            ]);
        },
        onError: (err, _variables, context) => {
            if (context?.previousData[0]) {
                Root_1.queryClient.setQueryData(queryKeyActivity, context.previousData[0]);
            }
            if (context?.previousData[1]) {
                Root_1.queryClient.setQueryData(queryKeyApproval, context.previousData[1]);
            }
            setNotification([
                {
                    type: strings_1.notificationStrings.ERROR,
                    message: 'There was a problem declining the request. Please try again.',
                },
            ]);
            logger.error(err);
        },
        onSettled: () => {
            setPageLoading(false);
        },
    });
};
exports.useDeclinePendingApprovalMutationQuery = useDeclinePendingApprovalMutationQuery;
const getTeamApproversQuery = ({ teamId, approverId }) => ({
    queryKey: approvalKeys.getTeamApprovers(teamId, approverId),
    queryFn: () => approvalRepository_1.ApprovalRepository.getTeamApprovers(teamId, approverId),
    enabled: !!teamId,
});
const useGetTeamApproversQuery = teamId => {
    return (0, react_query_1.useQuery)(getTeamApproversQuery(teamId));
};
exports.useGetTeamApproversQuery = useGetTeamApproversQuery;
const useForwardRequestMutation = () => {
    const { setShowConversationButtons } = (0, zustandStore_1.useZustand)();
    return (0, react_query_1.useMutation)({
        mutationFn: approvalRepository_1.ApprovalRepository.forwardRequest,
        onSuccess: async (_data, _variables) => {
            setShowConversationButtons(true);
        },
        onError: err => {
            logger.error(err);
        },
    });
};
exports.useForwardRequestMutation = useForwardRequestMutation;
const updateOnCancelData = async (subjectId) => {
    const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
    const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
    const queryKeys = [queryKeyActivity, queryKeyApproval];
    const previousDataCollection = await Promise.all(queryKeys.map(async (queryKey) => {
        await Root_1.queryClient.cancelQueries({ queryKey });
        const previousData = Root_1.queryClient.getQueryData(queryKey);
        if (previousData) {
            const approvedApprovals = previousData.groupedApprovals.approved.approvals;
            const approvalIndex = approvedApprovals.findIndex(approval => approval.subjectId === subjectId);
            if (approvalIndex !== -1) {
                await updateQueryData(previousData, queryKey, draft => {
                    const approvalToUpdate = draft.groupedApprovals.approved.approvals[approvalIndex];
                    if (!approvalToUpdate) {
                        return;
                    }
                    const formattedLastEdit = new Date(Date.now()).toISOString();
                    approvalToUpdate.outcome = appConstants_1.requestConstants.outcomes.canceled;
                    approvalToUpdate.request.lastEdit = formattedLastEdit;
                    draft.groupedApprovals.canceled.approvals.push({
                        ...approvalToUpdate,
                        isPending: false,
                        isApproved: false,
                        isDeclined: false,
                        isCancelled: true,
                    });
                    draft.groupedApprovals.canceled.count += 1;
                    draft.groupedApprovals.approved.approvals.splice(approvalIndex, 1);
                    draft.groupedApprovals.approved.count -= 1;
                    draft.assignedApprovals = draft.assignedApprovals.filter(assignedApproval => assignedApproval.subjectId !== subjectId);
                });
            }
        }
        return previousData;
    }));
    return previousDataCollection;
};
const useCancelApprovalMutationQuery = () => {
    const { setNotification, setPageLoading } = (0, zustandStore_1.useZustand)();
    const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
    const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
    return (0, react_query_1.useMutation)({
        mutationFn: approvalRepository_1.ApprovalRepository.cancelApproval,
        onMutate: async ({ subjectId }) => {
            setPageLoading(true);
            const previousCollection = await updateOnCancelData(subjectId);
            return { previousData: previousCollection };
        },
        onSuccess: () => {
            setNotification([
                {
                    type: strings_1.notificationStrings.SUCCESS,
                    message: `Request was ${(0, translationUtil_1.getCancelledWord)('canceled')}.`,
                },
            ]);
        },
        onError: (err, _variables, context) => {
            if (context?.previousData[0]) {
                Root_1.queryClient.setQueryData(queryKeyActivity, context.previousData[0]);
            }
            if (context?.previousData[1]) {
                Root_1.queryClient.setQueryData(queryKeyApproval, context.previousData[1]);
            }
            setNotification([
                {
                    type: strings_1.notificationStrings.ERROR,
                    message: `There was a problem ${(0, translationUtil_1.getCancelledWord)('canceling')} this card. Please try again.`,
                },
            ]);
            logger.error(err);
        },
        onSettled: () => {
            setPageLoading(false);
        },
    });
};
exports.useCancelApprovalMutationQuery = useCancelApprovalMutationQuery;
const updateOnSubmitData = async (variables) => {
    const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
    const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
    const queryKeys = [queryKeyActivity, queryKeyApproval];
    const previousDataCollection = await Promise.all(queryKeys.map(async (queryKey) => {
        await Root_1.queryClient.cancelQueries({ queryKey });
        const previousData = Root_1.queryClient.getQueryData(queryKey);
        if (previousData) {
            const pendingApprovals = previousData.groupedApprovals.pending.approvals;
            const pendingIndex = pendingApprovals.findIndex(approval => approval.request.requestId === variables.requestId);
            if (pendingIndex !== -1) {
                await updateQueryData(previousData, queryKey, draft => {
                    const pendingApproval = draft.groupedApprovals.pending.approvals[pendingIndex];
                    if (!pendingApproval) {
                        return;
                    }
                    const formattedLastEdit = new Date(Date.now()).toISOString();
                    pendingApproval.outcome = appConstants_1.requestConstants.outcomes.approved;
                    pendingApproval.context.isProcessing = 'true';
                    pendingApproval.request = {
                        ...pendingApproval.request,
                        lastEdit: formattedLastEdit,
                        virtualCardId: '',
                        state: appConstants_1.requestConstants.outcomes.approved,
                    };
                    draft.groupedApprovals.approved.approvals.push({
                        ...pendingApproval,
                        isPending: false,
                        isApproved: true,
                        isDeclined: false,
                        isCancelled: false,
                    });
                    draft.groupedApprovals.approved.count += 1;
                    draft.groupedApprovals.pending.approvals.splice(pendingIndex, 1);
                    draft.groupedApprovals.pending.count -= 1;
                });
            }
        }
        return previousData;
    }));
    return previousDataCollection;
};
const useSubmitApprovalMutationQuery = () => {
    const queryClient = (0, react_query_1.useQueryClient)();
    const { setNotification, setPageLoading } = (0, zustandStore_1.useZustand)();
    const queryKeyActivity = approvalKeys.combinedApprovalsData(true);
    const queryKeyApproval = approvalKeys.combinedApprovalsData(false);
    return (0, react_query_1.useMutation)({
        mutationFn: approvalRepository_1.ApprovalRepository.approve,
        onMutate: async (variables) => {
            const previousCollection = await updateOnSubmitData(variables);
            setPageLoading(false);
            return { previousData: previousCollection };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: query_keys_1.requestKeys.getRequestList(),
            });
            setNotification([
                {
                    type: strings_1.notificationStrings.SUCCESS,
                    message: 'The virtual card has been created.',
                },
            ]);
        },
        onError: (error, _variables, context) => {
            logger.error(error);
            if (context?.previousData[0]) {
                queryClient.setQueryData(queryKeyActivity, context.previousData[0]);
            }
            if (context?.previousData[1]) {
                queryClient.setQueryData(queryKeyApproval, context.previousData[1]);
            }
            if (error['response']?.status === 403) {
                setNotification([
                    {
                        type: strings_1.notificationStrings.WARNING,
                        message: String(error['response']?.data) || error.message,
                    },
                ]);
            }
            else if (error['response']?.status === 429) {
                setNotification([
                    {
                        type: strings_1.notificationStrings.WARNING,
                        message: 'Your company has exceeded the limit for approvals made per minute. ' +
                            'Please limit approvals to less than 10 per minute and try again later.',
                    },
                ]);
            }
            else {
                setNotification([
                    {
                        type: strings_1.notificationStrings.WARNING,
                        message: 'There was a problem creating the virtual card. Please try again.',
                    },
                ]);
            }
        },
    });
};
exports.useSubmitApprovalMutationQuery = useSubmitApprovalMutationQuery;
